
import map from 'lodash/map';
import Promise from 'bluebird';
import invariant from 'invariant';

import firebaseApp from '../config/firebase';
import { handleException } from '../app';
import { COLLABORATOR_ACTION_TYPES } from './constants';
import { getActiveProject } from '../projects';
import { postRequest } from '../../RequestUtil';
import {
    SEND_INVITE_CLOUD_FUNCTION_URL,
    SEND_COLLABORATOR_ACCEPTED_URL
} from './constants';

import type { Id, Dispatch } from '../types';
import type { CreateTimelineEventObject} from './types';


export function createProject(params: CreateProjectParamsObject): (dispatch: Dispatch) => Promise<string> {
  return async (dispatch: Dispatch): Promise<string> => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.CREATE_PROJECT
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const project = {
        name: params.name,
        date: params.date,
        createdBy: currentUser.uid,  //TODO params.createdBy || currentuser
        timezone: params.timezone
      };
      const projectRef = await db.ref('projects').push(project);
      const projectId = projectRef.key;

      await db.ref('collaborators').push({
        user: currentUser.uid,  //TODO params.createdby || current user
        project: projectId,
        role: 'owner'
      });

      if (params.collaboratorEmail) {
        await dispatch(inviteCollaborator(params.collaboratorEmail, projectId));
      }

      // const photoURL = await uploadProjectPhoto({
      //   projectId: projectId,
      //   uri: params.photoURI
      // });
      const photoURL = '';  //TODO

      await projectRef.child('photoURL').set(photoURL);
      dispatch({
        type: COLLABORATOR_ACTION_TYPES.RECEIVE_PROJECT_ID,
        payload: {
          projectId: projectId,
        }
      });
      return projectId;
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadTimelineEvents(): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.FETCH_TIMELINE_EVENTS
    });
    try {
      const activeProject = getActiveProject(getState());
      invariant(activeProject, 'Cannot load timeline without an active project.');
      const timelineEventsRef = firebaseApp
        .database()
        .ref('timelineEvents')
        .orderByChild('projectId')
        .equalTo(activeProject.id);
      await timelineEventsRef.on('value', (timelineEvents) => {
        dispatch({
          type: COLLABORATOR_ACTION_TYPES.RECEIVE_TIMELINE_EVENTS,
          payload: {
            timelineEvents: map(timelineEvents.toJSON(), (event, key) => ({
              uid: key,
              ...event
            })) || []
          }
        });
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function createTimelineEvent(params: CreateTimelineEventObject): (dispatch: Dispatch, getState: GetState) => Promise<void> {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.CREATE_TIMELINE_EVENT
    });
    try {
      const db = firebaseApp.database();
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const event = {
        name: params.name,
        dateTime: params.dateTime,
        location: params.location,
        details: params.details,
        projectId: params.projectId,
        createdBy: currentUser.uid
      };
      await db.ref('timelineEvents').push(event);
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadCollaborators(projectId: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.FETCH_COLLABORATORS
    });
    try {
      const db = firebaseApp.database();
      const collaboratorsRef = db
        .ref('collaborators')
        .orderByChild('project')
        .equalTo(projectId);
      collaboratorsRef.on('value', async c => {
        const collaboratorsJson = c.toJSON();
        const collaborators = await Promise.all(map(collaboratorsJson, async (collaborator, key) => {
          const userRef = db
            .ref('users')
            .child(collaborator.user);
          const user = await userRef.once('value');
          const userJson = user.toJSON();
          if (userJson !== null) {
            return {
              uid: key,
              email: userJson.email,
              username: userJson.username,
              name: userJson.name,
              firstName: userJson.firstName,
              lastName: userJson.lastName,
              avatarURL: userJson.avatarURL,
              notificationsEnabled: userJson.notificationsEnabled,
              ...collaborator
            };
          }
        }));
        dispatch({
          type: COLLABORATOR_ACTION_TYPES.RECEIVE_COLLABORATORS,
          payload: {
            collaborators
          }
        });
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function loadCollaboratorInvites(projectId: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.FETCH_COLLABORATOR_INVITES
    });
    try {
      const db = firebaseApp.database();
      const invitesRef = db
        .ref('collaboratorInvites')
        .orderByChild('project')
        .equalTo(projectId);
      invitesRef.on('value', invites => {
        dispatch({
          type: COLLABORATOR_ACTION_TYPES.RECEIVE_COLLABORATOR_INVITES,
          payload: {
            invites: map(invites.toJSON(), (invite, key) => ({
              uid: key,
              ...invite
            })) || []
          }
        });
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function addInvitedProjects(): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.ADD_INVITED_PROJECTS
    });
    try {
      const auth = firebaseApp.auth();
      const { currentUser } = auth;
      const db = firebaseApp.database();
      const invitesRef = db
        .ref('collaboratorInvites')
        .orderByChild('email')
        .equalTo(currentUser.email);
      const invites = await invitesRef.once('value');
      await Promise.all(
        map(invites.toJSON(), async (invite, key) => {
          await dispatch(createCollaborator(invite.project, currentUser.uid));
          await dispatch(deleteInvitation(key));
          await postRequest({
            url: SEND_COLLABORATOR_ACCEPTED_URL,
            headers: {
              'Content-Type': 'application/json'
            },
            body: {
              project: invite.project,
              user: currentUser.uid
            }
          });
        })
      );
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteInvitation(id: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.DELETE_COLLABORATOR_INVITE
    });
    try {
      const db = firebaseApp.database();
      await db.ref('collaboratorInvites').child(id).remove();
      dispatch({
        type: COLLABORATOR_ACTION_TYPES.RECEIVE_DELETED_COLLABORATOR_INVITE,
        payload: {
          id: id
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function createCollaborator(projectId: Id, userId: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.CREATE_COLLABORATOR
    });
    try {
      const db = firebaseApp.database();
      const value = await db.ref('collaborators').push({
        project: projectId,
        user: userId,
        role: 'guest'
      });
      dispatch({
        type: COLLABORATOR_ACTION_TYPES.RECEIVE_COLLABORATOR,
        payload: {
          collaborator: {
            uid: value.key
          }
        }
      })
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function deleteCollaborator(id: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.DELETE_COLLABORATOR
    });
    try {
      const db = firebaseApp.database();
      await db.ref('collaborators').child(id).remove();
      dispatch({
        type: COLLABORATOR_ACTION_TYPES.RECEIVE_DELETED_COLLABORATOR,
        payload: {
          id: id
        }
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}

export function inviteCollaborator(email: string, projectId: Id): (dispatch: Dispatch) => Promise<void> {
  return async (dispatch: Dispatch) => {
    dispatch({
      type: COLLABORATOR_ACTION_TYPES.INVITE_COLLABORATOR
    });
    try {
      const db = firebaseApp.database();
      const invite = await db.ref('collaboratorInvites').push({
        project: projectId,
        email
      });
      await postRequest({
        url: SEND_INVITE_CLOUD_FUNCTION_URL,
        query: {
          email,
          project: projectId
        }
      });
      dispatch({
        type: COLLABORATOR_ACTION_TYPES.RECEIVE_COLLABORATOR_INVITE,
        payload: invite.toJSON()
      });
    }
    catch (error) {
      dispatch(handleException(error));
    }
  };
}



// WEBPACK FOOTER //
// ./src/redux/collaborators/actions.js